import {Component, computed, inject, signal} from '@angular/core';
import {FormGroup} from "@angular/forms";
import {factoryBankDetailsResourceType} from "./configs/factory-bank-details-modal-configs";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  FactoryBankDetails,
  FactoryBankDetailsDialogData,
  FactoryBankDetailsFormField
} from "./types/factory-bank-details-modal.types";
import {MatButton} from "@angular/material/button";
import {FactoryBankDetailsModalService} from "./services/factory-bank-details-modal.service";
import {defaultFactoryBankDetails} from "./configs/default-factory-bank-details";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {
  FactoryBankDetailsFormComponent
} from "./components/factory-bank-details-form/factory-bank-details-form.component";
import {factoryBankDetailsFormFields} from "./configs/factory-bank-details-form-fields";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {
  FactoryBankDetailsTitleComponent
} from "./components/factory-bank-details-title/factory-bank-details-title.component";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-factory-bank-details-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    FactoryBankDetailsFormComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    FactoryBankDetailsTitleComponent,
    LoaderComponent,
  ],
  templateUrl: './factory-bank-details-modal.component.html',
  styleUrl: './factory-bank-details-modal.component.scss',
  providers: [ EditModeService ]
})
export class FactoryBankDetailsModalComponent extends BaseModalComponent {
  private factoryBankDetailsDialogData = inject<FactoryBankDetailsDialogData>(MAT_DIALOG_DATA);
  private factoryBankDetailsModalService: FactoryBankDetailsModalService = inject(FactoryBankDetailsModalService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);

  public readonly factoryBankDetailsResourceType = factoryBankDetailsResourceType;
  public factoryBankDetailsFormGroups = signal<Array<FormGroup>>([]);
  public factoryBankDetailsData = signal<Array<FactoryBankDetails>>([]);
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  public technicalParametersId = computed(() => this.factoryBankDetailsDialogData.parentId);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(factoryBankDetailsResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<FactoryBankDetailsFormField>(factoryBankDetailsFormFields);
    this.factoryBankDetailsFormGroups.update(forms  => [ ...forms, formGroup]);
    this.factoryBankDetailsData.update(data  => [ ...data, defaultFactoryBankDetails ]);
    this.isValid.set(false);
    this.noFirstData.set(!!this.factoryBankDetailsData()?.at(0)?.id);
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    this.loadingService.setIsLoadingState(true);
    await this.deleteRecord(factoryBankDetailsResourceType.path, id)
      .then(() => this.removeFactoryBankDetailsAtIndex(index))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  public updateFactoryBankDetailsData(data: FactoryBankDetails, index: number): void {
    this.factoryBankDetailsData()[index] = data;
    this.factoryBankDetailsFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
  }

  public deleteFactoryBankDetailsData(index: number): void {
    this.factoryBankDetailsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.factoryBankDetailsFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  public setLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private async initModal(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.modalStatesService.openModal(factoryBankDetailsResourceType.key);
    await this.factoryBankDetailsModalService.getDataInModal<FactoryBankDetails>(this.technicalParametersId())
      .then((data) => this.successFetch(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successFetch(data: Array<FactoryBankDetails>): void {
    this.factoryBankDetailsData.set(data);
    this.isValid.set(!!this.factoryBankDetailsData()?.at(0)?.id);
    this.noFirstData.set(!!this.factoryBankDetailsData()?.at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.factoryBankDetailsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.factoryBankDetailsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<FactoryBankDetailsFormField>(factoryBankDetailsFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.factoryBankDetailsFormGroups.set(formGroups);
  }

  private removeFactoryBankDetailsAtIndex(index: number): void {
    this.factoryBankDetailsFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.factoryBankDetailsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    if (!this.factoryBankDetailsData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }

    this.isValid.set(!!this.factoryBankDetailsData().length);
  }
}
